import { create } from 'zustand';
import { devtools, subscribeWithSelector } from 'zustand/middleware';
import { useShallow } from 'zustand/react/shallow';
import { offlineStorage, PendingRecording } from '@/services/offline-storage-service';
import { logger } from '@/utils/logger';

export type SyncStatus = 'idle' | 'syncing' | 'error' | 'offline';

export interface OfflineRecording extends Omit<PendingRecording, 'audioBlob' | 'audioData'> {
  // Simplified version without the large blob data for reactive state
  hasAudio: boolean;
  audioSizeBytes: number;
}

interface OfflineState {
  // Connection state
  isOnline: boolean;
  lastOnlineTime: Date | null;

  // Sync state
  syncStatus: SyncStatus;
  lastSyncTime: Date | null;
  syncError: string | null;

  // Pending recordings (without blob data for performance)
  pendingRecordings: OfflineRecording[];
  syncingRecordingId: string | null;

  // Statistics
  totalPendingSize: number; // in bytes
  failedAttempts: number;
}

interface OfflineActions {
  // Connection
  setOnline: (online: boolean) => void;

  // Sync management
  setSyncStatus: (status: SyncStatus) => void;
  setSyncError: (error: string | null) => void;
  startSync: (recordingId?: string) => void;
  completeSync: () => void;

  // Recording management
  refreshPendingRecordings: (userId: string) => Promise<void>;
  addPendingRecording: (recording: OfflineRecording) => void;
  removePendingRecording: (id: string) => void;
  updateRecordingStatus: (id: string, status: PendingRecording['status'], errorMessage?: string) => void;

  // Statistics
  incrementFailedAttempts: () => void;
  resetFailedAttempts: () => void;

  // Reset
  reset: () => void;
}

export type OfflineStore = OfflineState & OfflineActions;

const initialState: OfflineState = {
  isOnline: typeof navigator !== 'undefined' ? navigator.onLine : true,
  lastOnlineTime: null,
  syncStatus: 'idle',
  lastSyncTime: null,
  syncError: null,
  pendingRecordings: [],
  syncingRecordingId: null,
  totalPendingSize: 0,
  failedAttempts: 0,
};

export const useOfflineStore = create<OfflineStore>()(
  devtools(
    subscribeWithSelector((set, get) => ({
      ...initialState,

      setOnline: (online) => {
        const wasOffline = !get().isOnline;
        set({
          isOnline: online,
          lastOnlineTime: online ? new Date() : get().lastOnlineTime,
          syncStatus: online ? get().syncStatus : 'offline',
        }, false, 'setOnline');

        // Log transition
        if (wasOffline && online) {
          logger.info('[OfflineStore] Back online');
        } else if (!online) {
          logger.info('[OfflineStore] Gone offline');
        }
      },

      setSyncStatus: (status) => {
        set({ syncStatus: status }, false, 'setSyncStatus');
      },

      setSyncError: (error) => {
        set({
          syncError: error,
          syncStatus: error ? 'error' : get().syncStatus,
        }, false, 'setSyncError');
      },

      startSync: (recordingId) => {
        logger.info('[OfflineStore] Starting sync', recordingId ? `for ${recordingId}` : 'all');
        set({
          syncStatus: 'syncing',
          syncingRecordingId: recordingId || null,
          syncError: null,
        }, false, 'startSync');
      },

      completeSync: () => {
        logger.info('[OfflineStore] Sync completed');
        set({
          syncStatus: 'idle',
          lastSyncTime: new Date(),
          syncingRecordingId: null,
          syncError: null,
        }, false, 'completeSync');
      },

      refreshPendingRecordings: async (userId) => {
        try {
          const recordings = await offlineStorage.getRecordings(userId);

          const pendingRecordings: OfflineRecording[] = recordings.map((r) => ({
            id: r.id,
            title: r.title,
            template: r.template,
            timestamp: r.timestamp,
            duration: r.duration,
            user_id: r.user_id,
            status: r.status,
            attempts: r.attempts,
            lastAttempt: r.lastAttempt,
            nextAttempt: r.nextAttempt,
            meetingId: r.meetingId,
            transcriptId: r.transcriptId,
            transcriptText: r.transcriptText,
            audioSize: r.audioSize,
            audioMimeType: r.audioMimeType,
            errorMessage: r.errorMessage,
            transcription_model: r.transcription_model,
            // Computed fields
            hasAudio: Boolean(r.audioBlob && r.audioBlob.size > 0),
            audioSizeBytes: r.audioSize || r.audioBlob?.size || 0,
          }));

          const totalSize = pendingRecordings.reduce((sum, r) => sum + r.audioSizeBytes, 0);

          set({
            pendingRecordings,
            totalPendingSize: totalSize,
          }, false, 'refreshPendingRecordings');
        } catch (error) {
          logger.error('[OfflineStore] Failed to refresh recordings:', error);
        }
      },

      addPendingRecording: (recording) => {
        set((state) => ({
          pendingRecordings: [...state.pendingRecordings, recording],
          totalPendingSize: state.totalPendingSize + recording.audioSizeBytes,
        }), false, 'addPendingRecording');
      },

      removePendingRecording: (id) => {
        const recording = get().pendingRecordings.find((r) => r.id === id);
        set((state) => ({
          pendingRecordings: state.pendingRecordings.filter((r) => r.id !== id),
          totalPendingSize: state.totalPendingSize - (recording?.audioSizeBytes || 0),
        }), false, 'removePendingRecording');
      },

      updateRecordingStatus: (id, status, errorMessage) => {
        set((state) => ({
          pendingRecordings: state.pendingRecordings.map((r) =>
            r.id === id
              ? { ...r, status, errorMessage, attempts: r.attempts + 1, lastAttempt: Date.now() }
              : r
          ),
        }), false, 'updateRecordingStatus');
      },

      incrementFailedAttempts: () => {
        set((state) => ({
          failedAttempts: state.failedAttempts + 1,
        }), false, 'incrementFailedAttempts');
      },

      resetFailedAttempts: () => {
        set({ failedAttempts: 0 }, false, 'resetFailedAttempts');
      },

      reset: () => {
        set(initialState, false, 'reset');
      },
    })),
    { name: 'offline-store' }
  )
);

// Initialize online/offline listeners
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    useOfflineStore.getState().setOnline(true);
  });

  window.addEventListener('offline', () => {
    useOfflineStore.getState().setOnline(false);
  });
}

// Selector hooks
export const useIsOnline = () => useOfflineStore((state) => state.isOnline);
export const useSyncStatus = () => useOfflineStore((state) => state.syncStatus);
export const usePendingRecordings = () => useOfflineStore((state) => state.pendingRecordings);
export const usePendingCount = () => useOfflineStore((state) => state.pendingRecordings.length);
export const useTotalPendingSize = () => useOfflineStore((state) => state.totalPendingSize);

// Combined selectors - use useShallow to prevent infinite re-renders
export const useOfflineStatus = () => useOfflineStore(
  useShallow((state) => ({
    isOnline: state.isOnline,
    syncStatus: state.syncStatus,
    pendingCount: state.pendingRecordings.length,
    hasPending: state.pendingRecordings.length > 0,
    syncError: state.syncError,
  }))
);

// Format size helper
export const formatBytes = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};
